// DispatchDeck — Ship Slip helpers
// Slip lifecycle, numbering, aggregation, and printable HTML generation.
// Used by billing.js to render the slip section in the ticket detail view.

import {
  db,
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  Timestamp,
  query,
  orderBy,
  limit
} from '../firebase-config.js?v=1868';
// TASK-1354(b) integrated-gate — runTransaction imported from the pinned
// gstatic URL rather than added to firebase-config.js, which has 35 importers
// that would all need a lockstep buster bump (the same pattern ap.js,
// dispatch.js, maintenance.js and pretrip.js already use).
//
// 🔴 TASK-4825 — this line said **10.7.1** while firebase-config.js (which
// creates `db`) is on **10.8.0**, and every other static importer of this pattern
// (dispatch.js, billing.js) is on 10.8.0 too. So this module was the one static
// import in the main app graph pulling a SECOND, DIFFERENT copy of
// firebase-firestore.js onto every page load, and then calling ITS
// `runTransaction` with a `db` handle minted by the other copy. The gstatic URLs
// stay external through esbuild, so this shipped in the served bundle as well.
// Pinned to 10.8.0 to match `db`. Three DYNAMIC 10.7.1 imports remain elsewhere
// (billing.js ~:4394, maintenance.js ~:7366, jobboard.js ~:2100) — same class,
// lazy rather than eager, reported as a follow-up rather than swept in here.
import { runTransaction } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
// TASK-4825 — ONE retry loop for every `_counters` allocator in the app.
// See counterRetry.js for why it is an import and not a `window.*` handoff, and
// why the SERVER twins deliberately do not use it.
// 🔴 Bump this `?v=` in lockstep with views/billing.js and views/dispatch.js.
// COUNTER_RETRYABLE is imported, not re-declared, so "which failures are a lost
// race" has exactly ONE definition — the thing TASK-4825 spent a night collapsing.
import { allocateCounterWithRetry, COUNTER_RETRYABLE } from './counterRetry.js?v=1';

// ============================================
// Tenant helper (matches billing.js convention)
// ============================================
function resolveTenant(tenant) {
  return tenant === 'demo-tenant' ? 'demo' : tenant;
}

// ============================================
// SLIP NUMBERING — Per-tenant counter at tenants/{tenant}/_counters/slips
// Format: SS-1024 (starts at SS-1024 to leave room for legacy/manual slips)
//
// TASK-1354(b) integrated-gate — was a read-then-write with a documented
// "small race window acceptable at v1 volumes". That stopped being acceptable
// the moment TASK-1354(b) made the SHARED `_counters/{counterId}` update rule
// monotonic: two concurrent slip creators both read 1024, both try to write
// 1025, and the second is now DENIED rather than silently duplicating — i.e.
// a normal slip creation would fail outright.
//
// 🔴 TASK-4825 — AND THE SENTENCE THAT USED TO FOLLOW WAS FALSE. It read:
// "Firestore re-runs the loser against the committed value, so the second caller
// gets 1026 instead of an error." That is true of an ABORTED transaction. It is
// NOT true here: the monotonic rule answers the loser **permission-denied**,
// which the Web SDK treats as fatal and will not retry, so the second caller got
// no number and `createSlip` threw before it wrote anything. Measured on
// `_e2e_slipno` against real rules, one browser, Promise.all — see the numbers in
// counterRetry.js and tests/slip-number-allocator.spec.js. The comment was the
// whole bug: three lanes read that sentence and shipped the same defect.
//
// Fixed by the SHARED `allocateCounterWithRetry` (utils/counterRetry.js) that
// TASK-4823 wrote and measured for the ticket and invoice allocators — the same
// loop, not a fourth copy of it.
//
// 🔴 TASK-4832 — IT COULD NOT SELF-HEAL, AND THE ESCAPE HATCH WAS A FOOTGUN.
//
// firestore.rules ends the `_counters/{counterId}` block with
//     allow delete: if isTenantAdmin(tenantId);
// and calls it, in its own comment, "the escape hatch: monotonic means a bad
// value can never be lowered in place, so deleting the doc is how you force a
// clean re-seed from the live max on the next allocation."
//
// That sentence was TRUE for ticketNo / jobNo / invoiceNo-YYYYMM, each of which
// runs a max-probe on every call and takes `base = max(counter, observedMax)`.
// It was FALSE here. This allocator read the counter and nothing else, so
// deleting the doc did not re-seed from anything — it restarted the sequence at
// SS-1024 and re-issued numbers already printed on paper slips. MEASURED, not
// argued: `tests/slip-counter-self-heal.spec.js` seeds SS-1024..SS-1026 on a
// throwaway tenant, deletes the counter, allocates again and gets SS-1024 back
// against the pre-fix allocator. The documented repair was the collision.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY THE PROBE IS NOT A `collectionGroup` OVER `slips`
// ─────────────────────────────────────────────────────────────────────────────
// The obvious probe — "read every ticket's `slips` subcollection and take the
// max" — is the one thing that must NOT be built, for three independent reasons,
// any one of which is disqualifying:
//   1. A collection-group query is DATABASE-WIDE. `collectionGroup('slips')`
//      spans every tenant, which is the cross-tenant leak dispatch.js already
//      carries a standing rule against ("NEVER collectionGroup", :10062, :27180).
//   2. It would be DENIED anyway. Collection-group reads are authorised by a
//      rule matching `/{path=**}/slips/{slipId}`; firestore.rules has no such
//      match, only the path-scoped `match /slips/{slipId}` nested under
//      tickets. Asserted from the browser in the spec, not assumed.
//   3. It would cost a read per SLIP. On a busy tenant that is thousands of
//      document reads on the path a driver takes to record a load.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHAT IT DOES INSTEAD: ONE denormalised number, ONE `limit(1)` read
// ─────────────────────────────────────────────────────────────────────────────
// The allocator stamps `slipMaxNo` — the number it just issued, as an INT —
// onto the ticket the slip is being added to, in its OWN monotonic transaction
// (`_stampSlipMaxNo`), run AFTER the counter advances and BEFORE the slip is
// written. One writer, monotonic (never lowered), and ORDERED with slip
// persistence: a slip document only ever exists once its stamp has been
// written or been refused by the rules. That closes the three COD
// Code-Reviewer P1s against the first cut (which stamped from the best-effort,
// unordered `recomputeAndPersistTicketSlipTotals`): no reversed-order
// recomputes, no mark lost after a number was handed out, no lowering when the
// top slip is hard-deleted. It is deliberately NOT inside the counter
// transaction: a ticket write the rules refuse (a locked ticket; a
// viewUploadOnly member, who may create slips but not touch the ticket doc —
// firestore.rules:8244 vs :7231) would fail the WHOLE transaction, counter
// included, and the biller would read the contention copy for a denial they
// cannot see (COD round 2, Code Reviewer + AuthZ). A refused stamp is skipped
// and logged; the number is still issued. The probe is then the same shape as
// the ticket allocator's:
//     tenants/{tid}/tickets  orderBy('slipMaxNo','desc')  limit(1)
// ONE document read, tenant-scoped, and served by Firestore's AUTOMATIC
// single-field index — so this needs NO index deploy (and therefore never goes
// anywhere near `firebase deploy --only firestore:indexes --force`, which nukes
// TTLs). Cost is FLAT in tenant size, measured in the spec at 2 vs 252 tickets.
//
// 🔴 THIS IS ONLY POSSIBLE BECAUSE THERE IS NO LEGACY SLIP DATA TO BACKFILL.
// Verified read-only with the Admin SDK on 2026-09-09 AND again on 2026-09-24
// (this lane, all tenants): ZERO slip documents exist anywhere in the database
// and no `_counters/slips` doc exists on any tenant — slip creation was dead for
// 33 days and was only repaired on 9/09 (v1.15.1901). So every slip that will
// ever exist is stamped by this code, and the probe has no blind history behind
// it. If that stops being true (an import, a restore): backfill `slipMaxNo` on
// every ticket that received slips, THEN delete `_counters/slips` so the next
// allocation re-seeds from the backfilled max. The order matters — a backfill
// alone lands more than SLIP_SELF_HEAL_MAX_JUMP above a live counter and is
// IGNORED by design (see decideSlipCounterBase); the counter delete is what
// makes it authoritative. Set `_counters/slipsHighWater` to the same max in the
// same pass (admin SDK) so the heal has its primary witness too.
//
// 🔴 THE HIGH-WATER COUNTER — `_counters/slipsHighWater` (integration COD,
// 2026-09-25). The ticket stamp alone left two holes, both Code Reviewer P1s:
//   (a) a stamp the RULES refuse (an invoiced/locked ticket — tkUpdOk's
//       content lock; or a viewUploadOnly member, who may create slips but
//       not touch the ticket doc) left an issued number invisible to the
//       probe, so a later counter delete could re-issue it;
//   (b) `slipMaxNo` is client-writable and the rules do not pin it (see
//       below), so any writing member could LOWER the mark.
// Both are closed by a SECOND counter doc written in the SAME transaction as
// `_counters/slips`, holding the highest number ever issued. It needs NO rules
// change, because the existing `_counters/{counterId}` block already gives it
// exactly the properties wanted: create/update by canWriteTenant (the same
// tier that creates slips, so no issuer is ever refused), update only STRICTLY
// UPWARD and by at most +1,000,000, delete by a tenant admin only. So no
// client can lower it, a locked ticket cannot block it, and it is read INSIDE
// the transaction (a doc get, not a query) — atomic with the seed. Deleting
// `_counters/slips` (the escape hatch) now re-seeds from max(high-water,
// ticket stamps), i.e. from the highest number actually issued. An admin who
// deliberately deletes BOTH is back to the ticket stamps alone — an admin-only
// action, not a client one.
// The ticket stamp is kept: it is the fallback when the high-water doc has
// been deleted, and it is what the E2E suite already proves ordering on.
// A refused stamp is still SKIPPED and logged, never fatal. Whether Add Ship
// Slip should be offered on a locked ticket at all is a product call for
// Chris; it no longer affects numbering safety.
//
// ⚠️ THE PROBE IS NOT ATOMIC WITH THE SEED — Firestore client transactions
// cannot contain a query. So a seed/heal from an ABSENT counter never trusts a
// probe older than its own attempt: the transaction refuses to seed on a probe
// taken before a previous attempt ran, and the thunk re-probes (see
// `slipReprobe`). That closes the realistic shape of the race COD round 2
// named (A stuck in the 30s retry loop while B issues a number and an admin
// deletes the counter → A seeds from its stale max and re-issues B's number).
// The residual is the milliseconds between a fresh probe and its transaction;
// closing THAT needs server-side allocation, the wall TASK-1354 already named.
//
// 🔴 `slipMaxNo` IS CLIENT-WRITABLE AND THE RULES DO NOT YET PIN IT. Any
// writing member can raw-SDK LOWER the mark on the ticket holding the
// tenant-wide max, and the next escape-hatch delete would then re-seed below
// printed numbers (COD Adversary, both rounds). The two-line tkUpdOk clause
// that closes it (`rq.get('slipMaxNo',0) is int && rq.get('slipMaxNo',0) >=
// cur.get('slipMaxNo',0)`) was written, emulator-tested green on its own
// assertions, and REVERTED: it tips the tickets update rule back over
// Firestore's 1,000-expression evaluation ceiling on the soft-delete path
// (tests/rules/task4577-repointReason.test.mjs:185 went red — "this denial
// came from the evaluator giving up, not from a rule"). TASK-4578 left that
// path 7 pad units of headroom on 2026-08-21 and later clauses spent it; the
// clause ships once headroom is bought back (TASK-4578 named the next lever),
// and its assertions wait as `todo` in tests/rules/task4832-slipMaxNo.test.mjs.
// LOWERING a ticket mark no longer re-issues anything while
// `_counters/slipsHighWater` exists (see above): the heal takes the max of
// both. Raising a mark is bounded by SLIP_SELF_HEAL_MAX_JUMP with a counter
// present and by the rules CREATE ceiling without one, and is strictly weaker
// than the raw `_counters` write the same tier already has (an accepted speed
// bump) — a raised number SKIPS numbers, it never duplicates one.
//
// ⚠️ WHAT HAPPENS WHEN TWO CLIENTS SELF-HEAL AT ONCE: nothing new. Both probe
// (a read, no contention), both compute the same base, both write base+1 into
// the SAME monotonic counter inside a transaction. Firestore serialises them,
// the loser is answered permission-denied, and `allocateCounterWithRetry`
// re-runs it against the committed value — the ordinary contention path this
// module already survives at N=25. Asserted in the spec by racing 5 allocations
// immediately after a counter delete. It is not a stampede because the probe is
// ONE `limit(1)` read per caller, never a scan, and it runs ONCE per caller
// OUTSIDE the retry loop (see counterRetry.js's contract for the thunk).
// ============================================

// The seed baked into an EMPTY sequence — the first slip on a fresh tenant is
// SS-1024. Exported so the tests read it back instead of hard-coding a copy.
export const SLIP_NO_SEED = 1023;

// A heal on top of an EXISTING counter may move the sequence by at most this
// much. Seeding an ABSENT counter from the live max stays unbounded — that is
// what the seed is for, and it is exactly the counter-was-deleted repair.
// Rationale mirrors the invoice allocator's INVOICE_SELF_HEAL_MAX_JUMP: the
// honest reasons for a gap on top of a live counter are a stale bundle
// mid-rollout or an admin backfill, and those move by single digits.
export const SLIP_SELF_HEAL_MAX_JUMP = 10000;

// firestore.rules refuses a counter CREATE with `value > 100000000`. A probe
// result at or above that would be minted into a write the rules then DENY,
// stranding every future allocation behind an opaque permission error. So it is
// refused HERE, by name, where the message can say which number is lying.
export const SLIP_COUNTER_MAX_SEED = 100000000;

// The high-water counter doc id under tenants/{tid}/_counters (see the header).
// firestore.rules caps a counter UPDATE at +1,000,000 per write; the step is
// clamped to that so the high-water write can never be the thing that gets a
// whole allocation denied.
export const SLIP_HIGH_WATER_ID = 'slipsHighWater';
export const SLIP_HIGH_WATER_MAX_STEP = 1000000;

/**
 * What to write to the high-water doc, as a pure function (unit-tested).
 * @param {{issued: number, hw: number|null, hwExists: boolean}} input
 *   issued    the number this allocation just issued
 *   hw        the high-water value read in the transaction (null if absent or unusable)
 *   hwExists  whether the doc exists at all (an unusable value on an existing
 *             doc cannot be updated past the rules, so it is left alone)
 * @returns {{write: boolean, value?: number, create?: boolean, reason: string}}
 */
export function nextSlipHighWater({ issued, hw, hwExists }) {
  if (!Number.isInteger(issued) || issued <= 0) return { write: false, reason: 'bad-issued' };
  if (!hwExists) {
    // The rules refuse a counter CREATE above the ceiling — and a refused write
    // would fail the WHOLE transaction, counter advance included. Skip it; the
    // number is then unwitnessed and slipNumberWitnessed() decides.
    if (issued > SLIP_COUNTER_MAX_SEED) return { write: false, reason: 'above-create-ceiling' };
    return { write: true, create: true, value: issued, reason: 'create' };
  }
  if (!Number.isInteger(hw)) return { write: false, reason: 'unusable-existing' };
  if (issued <= hw) return { write: false, reason: 'already-higher' };
  const value = Math.min(issued, hw + SLIP_HIGH_WATER_MAX_STEP);
  return { write: true, create: false, value, reason: value === issued ? 'advance' : 'advance-clamped' };
}

/**
 * May a slip carrying `issued` be written? Only if the HIGH-WATER COUNTER now
 * holds ≥ issued (COD rounds 2+3, Code Reviewer P1). The ticket stamp is
 * deliberately NOT accepted as a witness: it is client-writable, and the heal
 * may legitimately ignore a ticket mark at/above the counter ceiling — so a
 * number witnessed only by a stamp could be re-issued. With this rule every
 * written slip is covered by a record no client can lower, which is also what
 * makes ignoring a ceiling ticket mark safe. When the high-water cannot cover
 * the number (clamped / unusable / uncreatable), the allocation is REFUSED and
 * the number becomes a harmless gap. Pathological only: the high-water must lag
 * the counter by more than +1,000,000, and each refused attempt still advances
 * it by that step, so it catches itself up.
 * `stamp` is accepted for the call-site shape and ignored on purpose.
 * @param {{issued: number, hwNext: object|null, stamp?: string}} input
 */
export function slipNumberWitnessed({ issued, hwNext }) {
  if (!hwNext) return false;
  return hwNext.reason === 'already-higher'
    || (hwNext.write === true && Number.isInteger(hwNext.value) && hwNext.value >= issued);
}

// THE FORMAT CONTRACT, for tooling and a future `slipMaxNo` backfill script:
// strict on purpose, so a manual or imported slip numbered "1024" or
// "SS-1024-A" can never anchor the sequence. The allocator itself no longer
// parses anything — it stamps the int it issued — so this is exported for
// scripts and tests, not called on the hot path. Unparseable → null.
export function parseSlipNo(slipNumber) {
  const m = /^SS-(\d+)$/.exec(String(slipNumber || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * THE SELF-HEAL DECISION, AS A PURE FUNCTION.
 *
 * Extracted so it can be proven in node (`tests/slip-self-heal.unit.cjs`)
 * without Firestore, a browser or a transaction — the branch table is the part
 * that is easy to get quietly wrong, and it is the part a browser suite proves
 * least well. Returns a DECISION OBJECT, not a bare number, so a test can assert
 * WHICH branch fired rather than inferring it from an integer that two different
 * branches could both have produced.
 *
 * @param {{current: number|null, observedMax: number|null, probeFailed: boolean}} input
 *   current      value read from `_counters/slips` inside the transaction, or null
 *   observedMax  highest `slipMaxNo` seen by the probe, or null
 *   probeFailed  the probe threw (and was retried) rather than returning null
 * @returns {{action: 'seed'|'heal'|'counter'|'refuse', base: number|null, reason: string, observedMax?: number}}
 */
export function decideSlipCounterBase({ current, observedMax, probeFailed }) {
  const haveCounter = Number.isFinite(current);
  const haveMax = Number.isFinite(observedMax);

  // Nothing to read and nothing to derive from. FAIL CLOSED — the sibling
  // allocators' rule, and jobNo's comment says why in one line: never `return 1`
  // here. A blind seed on a tenant sitting at SS-4200 mints SS-1024 forever.
  if (!haveCounter && probeFailed) {
    return { action: 'refuse', base: null, reason: 'no-counter-and-probe-failed' };
  }

  // Genuinely empty sequence — no counter, no numbered slip anywhere.
  if (!haveCounter && !haveMax) {
    return { action: 'seed', base: SLIP_NO_SEED, reason: 'empty-sequence' };
  }

  // THE REPAIR THIS TASK EXISTS FOR: the counter is gone, slips are not.
  if (!haveCounter) {
    if (observedMax >= SLIP_COUNTER_MAX_SEED) {
      return { action: 'refuse', base: null, observedMax, reason: 'seed-above-rules-ceiling' };
    }
    return { action: 'heal', base: observedMax, reason: 'reseed-from-live-max' };
  }

  // Counter exists and is the authority.
  if (!haveMax) return { action: 'counter', base: current, reason: 'counter-only' };

  // 🔴 AN IMPLAUSIBLE PROBE RESULT MUST NOT STOP FIELD WORK, AND MUST NOT BE
  // TRUSTED EITHER. `slipMaxNo` sits on a ticket doc, and firestore.rules lets
  // any writing member update a ticket — so a mistyped or tampered value could
  // otherwise jump the counter permanently, or (past the rules' +1,000,000
  // per-write bound) get DENIED and strand allocation behind an opaque error.
  // The invoice allocator REFUSES in this case because an invoice number is
  // customer-facing paperwork. A slip is not: refusing here would stop a driver
  // recording a load over a number nobody has looked at. So when there IS a
  // counter to fall back on, the implausible max is IGNORED — never silently:
  // the caller logs it (console.error + window.logError) so it is visible, and
  // the counter, which is monotonic and cannot have been walked backwards,
  // continues the sequence.
  if (observedMax - current > SLIP_SELF_HEAL_MAX_JUMP) {
    return { action: 'counter', base: current, observedMax, reason: 'implausible-jump-ignored' };
  }

  if (observedMax > current) {
    return { action: 'heal', base: observedMax, reason: 'counter-behind-live-max' };
  }
  return { action: 'counter', base: current, reason: 'counter-ahead' };
}

// ONE `limit(1)` read. Tickets carrying no `slipMaxNo` are dropped by the
// orderBy, which is exactly right: a ticket with no slips must never anchor the
// sequence. Served by the AUTOMATIC single-field index — no composite index, no
// index deploy. Returns `{ maxNo, ticketId }` or null (no ticket carries one).
async function _probeMaxSlipNo(tenant) {
  const snap = await getDocs(query(
    collection(db, 'tenants', tenant, 'tickets'),
    orderBy('slipMaxNo', 'desc'),
    limit(1)
  ));
  if (snap.docs.length === 0) return null;
  const maxNo = snap.docs[0].data().slipMaxNo;
  // 🔴 A TOP RESULT THAT IS NOT A NON-NEGATIVE INTEGER IS A FAILED PROBE, NOT AN
  // EMPTY ONE. Firestore orders values by TYPE before value (null < boolean <
  // number < string ...), so a string `slipMaxNo` on ANY ticket sorts ABOVE
  // every real number in a desc orderBy and is the one doc this limit(1)
  // returns. Returning null there would read as "no numbered slip anywhere"
  // and, with no counter, blind-seed SS-1024 — the exact re-issue this probe
  // exists to prevent. A float or a negative is the other shape: it would be
  // minted into `value = max + 1`, which firestore.rules refuses (`value is int`,
  // `value > 0`) as permission-denied — retryable, so the loop would burn its
  // 30s and the biller would read the contention copy for a number nobody can
  // see (2nd-review 🔴). Thrown instead, naming the ticket: with a counter the
  // counter carries on (`counter-only`); without one the allocator REFUSES.
  if (!Number.isInteger(maxNo) || maxNo < 0) {
    throw new Error(`slipMaxNo on ticket ${snap.docs[0].id} is not a whole number (${JSON.stringify(maxNo)}) — cannot trust the live max`);
  }
  return { maxNo, ticketId: snap.docs[0].id };
}

// THE STAMP — its own transaction on the ticket, MONOTONIC (never lowers an
// existing mark), run after the counter advanced and before the slip is
// written. Returns what happened rather than throwing on a rules denial:
//   'written' | 'already-higher' | 'no-ticket' | 'denied'
// Anything else (offline, deadline) is thrown: the number stays a harmless gap
// because createSlip has not written the slip yet, and the biller gets the
// connectivity copy. Contention on the ticket doc is an ABORTED the SDK re-runs
// by itself — the monotonic rule that makes the COUNTER answer permission-denied
// does not apply here, so no outer retry loop is needed.
async function _stampSlipMaxNo(tenant, ticketId, value) {
  const ref = doc(db, 'tenants', tenant, 'tickets', ticketId);
  try {
    return await runTransaction(db, async (txn) => {
      const s = await txn.get(ref);
      if (!s.exists()) return 'no-ticket';
      const cur = s.data().slipMaxNo;
      if (Number.isFinite(cur) && cur >= value) return 'already-higher';
      txn.update(ref, { slipMaxNo: value });
      return 'written';
    });
  } catch (e) {
    if (e?.code === 'permission-denied') return 'denied';
    throw e;
  }
}

/**
 * @param {string} tenantRaw
 * @param {string|null} ticketId  the ticket the slip is being added to. When
 *   given, the issued number is stamped onto it as `slipMaxNo` (see the
 *   header) before this resolves. Omitted by diagnostics/tests that only want
 *   a number.
 * @param {{afterProbe?: () => Promise<void>}} [opts]  DIAGNOSTICS ONLY — a hook
 *   the E2E suite uses to interleave another client's allocation and a counter
 *   delete between the initial probe and the transaction. Not for callers.
 */
export async function getNextSlipNumber(tenantRaw, ticketId = null, opts = {}) {
  const tenant = resolveTenant(tenantRaw);

  // Best-effort once the counter exists (the counter is then the authority) but
  // REQUIRED to seed it. One retry, because a numbering failure is nearly always
  // a transient read and the caller is a driver in the field.
  const runProbe = async () => {
    let probe = null;   // { maxNo, ticketId } | null
    let err = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        probe = await _probeMaxSlipNo(tenant);
        err = null;
        break;
      } catch (e) {
        err = e;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 400));
      }
    }
    return { probe, err };
  };

  // THE PROBE RUNS ONCE, OUTSIDE THE RETRY LOOP — the contract
  // allocateCounterWithRetry documents — for the COUNTER-PRESENT path: by the
  // time we are retrying, the counter has already moved PAST any max the probe
  // could have seen, so re-probing every round would cost a query per round and
  // change nothing. The COUNTER-ABSENT path is the exception, and the thunk
  // below re-probes for it: a seed must never trust a max taken before an
  // earlier attempt ran (see `slipReprobe` in _getNextSlipNumberTxn).
  let { probe, err: probeErr } = await runProbe();
  if (typeof opts.afterProbe === 'function') await opts.afterProbe();

  let reprobe = false;          // set by the txn when it refused to seed on a stale probe
  const thunk = async () => {
    let freshForThisAttempt = false;
    if (reprobe) {
      ({ probe, err: probeErr } = await runProbe());
      reprobe = false;
      freshForThisAttempt = true;
    }
    try {
      return await _getNextSlipNumberTxn(tenant, probe ? probe.maxNo : null, probe?.ticketId || null, probeErr, freshForThisAttempt);
    } catch (e) {
      if (e && e.slipReprobe) reprobe = true;
      throw e;
    }
  };
  const observedMax = () => (probe ? probe.maxNo : null);

  // 🔴 TASK-4832 — TAG THE ERROR, DO NOT REPLACE IT.
  //
  // When the retry is EXHAUSTED, allocateCounterWithRetry re-throws `lastErr`
  // verbatim, and on this counter that is almost always a raw FirebaseError with
  // code `permission-denied` — because the monotonic rule answers the LOSER of a
  // race exactly the same way it answers someone with no access at all. Handed
  // straight to a toast that reads "Error saving slip: Missing or insufficient
  // permissions.", which sends the biller hunting an access bug that does not
  // exist.
  //
  // The fix is a FLAG, not a wrapper: re-throwing a new error would change
  // `.code` and `.message`, which tests/slip-number-allocator.spec.js asserts on
  // (every bounded failure must carry a real error code). So the SAME object is
  // re-thrown with one extra own-property, and slipSaveFailedMessage below turns
  // it into words. The property is set defensively — a frozen error must never
  // turn a numbering failure into a TypeError.
  //
  // ⚠️ HONEST LIMIT, and the copy below says it out loud: from inside the loop a
  // lost race and a GENUINE authz denial are indistinguishable (counterRetry.js
  // says the same thing about itself). So the flag means "the allocator gave up",
  // not "this was contention" — and the message must not promise otherwise.
  //
  // 🔴 TAG ONLY A LOST-RACE CODE, NOT EVERY FAILURE. Measured on a throwaway
  // tenant: a first cut tagged whatever escaped, and a plain TypeError
  // ("Cannot read properties of null") came back wearing the contention copy —
  // and, worse, the `refuse` decisions below (which carry a written explanation
  // naming the offending slip number) would have had that explanation replaced
  // by "several slips saving at once". So the tag is gated on the SAME retryable
  // set the loop itself uses, imported rather than re-declared.
  let result;
  try {
    result = await allocateCounterWithRetry('slip number', thunk);
  } catch (e) {
    if (COUNTER_RETRYABLE.has(e?.code)) {
      try { e.slipNumberAllocationFailed = true; } catch (_) { /* frozen error */ }
    }
    throw e;
  }
  const { value, decision, hwNext } = result;   // result.ticketMaxIgnored read below

  // The stamp, ordered BEFORE the slip write (createSlip's addDoc runs after
  // this returns). A denial is skipped and counted below; anything else throws
  // and the number becomes a gap, never a duplicate.
  const stamp = ticketId ? await _stampSlipMaxNo(tenant, ticketId, value) : 'none';

  // Diagnostics run AFTER the transactions resolve, never inside a callback:
  // the SDK re-runs the callback on contention and the retry loop re-runs the
  // whole transaction up to 64 times, so a log inside it can write dozens of
  // errorLogs rows for ONE allocation (2nd-review ℹ️). Three things are worth a
  // row, none of them silent:
  //   • a HEAL — the counter moved to a max it did not issue. Ordinary tenants
  //     never do this; every occurrence is either the escape hatch being used or
  //     a `slipMaxNo` that arrived from outside the allocator (COD Adversary:
  //     a ≤10,000 skew must be VISIBLE, not merely tolerated).
  //   • an IGNORED implausible max — a guard that fails open without a trace is
  //     the failure it was meant to catch.
  //   • a high-water write that could not track the issued number (clamped to
  //     the rules' step, or an existing doc with an unusable value) — the heal's
  //     main witness is lagging, which must be visible.
  // A DENIED ticket stamp (locked ticket, or a member the rules let create slips
  // but not touch the ticket doc) is no longer a numbering risk — the
  // high-water counter holds the number — so it is a console note, not a row.
  //   • a ticket mark at/above the ceiling IGNORED in favour of the high-water.
  const hwLag = hwNext && (hwNext.reason === 'advance-clamped' || hwNext.reason === 'unusable-existing'
    || hwNext.reason === 'above-create-ceiling');
  // Only a caller that will persist a slip (it passed a ticket) needs a witness.
  const witnessed = !ticketId || slipNumberWitnessed({ issued: value, hwNext, stamp });
  if (stamp === 'denied' && witnessed) {
    console.info(`[slipHelpers] SS-${value}: ticket ${ticketId} refused the slipMaxNo stamp (locked ticket or upload-only login); the high-water counter holds it`);
  }
  const rows = [];
  if (decision.action === 'heal') rows.push(['SLIP_SELF_HEAL', `[slipHelpers] counter healed to SS-${value} from live max ${decision.base} (${decision.reason})`]);
  if (decision.reason === 'implausible-jump-ignored') rows.push(['SLIP_SELF_HEAL_IMPLAUSIBLE', `[slipHelpers] ignoring implausible slip max ${decision.observedMax} (counter is at ${decision.base}) — the counter continues the sequence`]);
  if (result.ticketMaxIgnored != null) rows.push(['SLIP_TICKET_MAX_IGNORED', `[slipHelpers] ignoring a ticket slipMaxNo of ${result.ticketMaxIgnored} (at/above the counter ceiling) — the high-water counter was used instead`]);
  if (hwLag) rows.push(['SLIP_HIGH_WATER_LAG', `[slipHelpers] SS-${value} issued but the slip high-water counter could not record it (${hwNext.reason})`]);
  if (!witnessed) rows.push(['SLIP_NUMBER_UNWITNESSED', `[slipHelpers] SS-${value} refused: the high-water counter cannot record it (${hwNext?.reason})`]);
  for (const [operation, msg] of rows) {
    console.error(msg);
    window.logError?.(new Error(msg), {
      module: 'billing', operation,
      tenant, ticketId, value, reason: decision.reason, observedMax: observedMax(), stamp, highWater: hwNext?.reason,
    });
  }
  if (!witnessed) {
    // Refuse BEFORE createSlip writes anything: the number becomes a gap, never
    // a slip a later counter delete could re-issue. Surfaced verbatim by
    // slipSaveFailedMessage (slipNumberingRefused).
    const err = new Error(`Slip number SS-${value} could not be recorded safely (the slip number record is behind). `
      + 'Press Save again — each try catches the record up. If it keeps happening, ask an admin to check the slip counters.');
    err.slipNumberingRefused = true;
    throw err;
  }
  return `SS-${value}`;
}

/**
 * Plain-words failure for a biller whose slip did not save.
 *
 * 🔴 THE ONE THING THIS MUST NEVER DO IS CLAIM THE SLIP FAILED TO SAVE WHEN IT
 * DID. `saveShipSlip`'s try block does not end at the write — it also covers the
 * re-read, the re-render and the print that follow a SUCCESSFUL create/update.
 * So the caller passes `persisted`, flipped to true the instant createSlip /
 * updateSlip resolves, and the two halves get different words:
 *
 *   persisted === false → nothing was written. Every path that can throw before
 *     this point (getNextSlipNumber, addDoc, updateDoc) leaves Firestore
 *     untouched: the number is allocated BEFORE addDoc, so there is never a
 *     half-written slip, and a rejected addDoc rolls its local mutation back.
 *   persisted === true  → THE SLIP IS SAVED. Only the refresh/print after it
 *     failed. Saying "couldn't save" here would send a biller to re-enter a slip
 *     that already exists — a duplicate on a money surface.
 *
 * @param {any} err          whatever escaped the try block
 * @param {{persisted?: boolean, editing?: boolean, slipNumber?: string}} ctx
 */
export function slipSaveFailedMessage(err, ctx = {}) {
  const { persisted = false, editing = false, slipNumber = '' } = ctx;
  const raw = String((err && err.message) || err || '');
  const named = slipNumber ? `Slip ${slipNumber}` : 'The slip';

  if (persisted) {
    return `${named} was saved. The screen couldn't refresh afterwards — reopen the ticket to see it. `
      + 'Do not re-enter it.';
  }

  // The numbering REFUSED, with a written reason that names the specific thing
  // that is wrong (a slip numbered above the counter ceiling; a probe that could
  // not run on a tenant with no counter). Surfaced verbatim — flattening it into
  // the generic copy would throw away the only information the biller can act on.
  if (err && err.slipNumberingRefused) {
    return `${raw} Nothing was saved and your entries are still here.`;
  }

  // Connectivity FIRST. `unavailable` / `deadline-exceeded` are in
  // COUNTER_RETRYABLE, so a driver with no signal also exhausts the loop and
  // arrives here TAGGED — and "several slips at once" would be the wrong story
  // for him (COD Code Reviewer P2 + 2nd review). The offline words win.
  const offline = /offline|unavailable|network|deadline|failed to get document|backend/i.test(raw);
  if (offline) {
    return `Couldn't reach the database, so ${editing ? 'the change was not saved' : 'nothing was saved'} `
      + '— your entries are still here. Check your connection and press Save again.';
  }

  // The allocator gave up. This is the case that used to read as an access bug.
  if (err && err.slipNumberAllocationFailed) {
    return "Couldn't get a slip number, so nothing was saved — your entries are still here. "
      + 'This is usually several slips being saved at the same moment: wait a few seconds and press Save again. '
      + "If it keeps failing, your login may not have permission to create slips.";
  }

  const denied = /permission|insufficient|denied/i.test(raw);
  if (denied) {
    return `${editing ? 'That change' : 'The slip'} was not saved — this login is not allowed to `
      + `${editing ? 'edit' : 'create'} slips on this ticket. Your entries are still here. Ask an admin for `
      + 'dispatch or billing access, then press Save again.';
  }

  return `Couldn't save the slip — ${raw || 'the save failed'}. Nothing was saved and your entries are still here. `
    + 'Try again in a moment.';
}

// ONE fresh transaction per call — the contract allocateCounterWithRetry requires.
// `tenant` is already resolved by the caller so resolveTenant does not re-run per
// attempt (it is cheap, but the thunk must stay free of anything that could
// change between attempts). `observedMax` / `probeErr` are likewise computed once
// and closed over, never re-derived here.
// Resolves to `{ value, decision }` — the number and WHICH branch issued it.
// The caller stamps and logs from that after the transaction.
//
// `probeFreshForThisAttempt` is the seed guard: when the counter is ABSENT and
// the probe in hand was taken before an earlier attempt ran, this refuses to
// seed and throws a RETRYABLE `slipReprobe` error instead. The thunk re-probes
// and the next attempt seeds from a max that post-dates every allocation the
// previous attempt could have raced. Costs one extra read on the rare
// no-counter path only; the counter-present path never pays it.
async function _getNextSlipNumberTxn(tenant, observedMax, observedOnTicket, probeErr, probeFreshForThisAttempt) {
  const counterRef = doc(db, 'tenants', tenant, '_counters', 'slips');
  const hwRef = doc(db, 'tenants', tenant, '_counters', SLIP_HIGH_WATER_ID);
  return await runTransaction(db, async (txn) => {
    // Both reads before any write (a Firestore transaction requirement).
    const snap = await txn.get(counterRef);
    const hwSnap = await txn.get(hwRef);
    const hwRaw = hwSnap.exists() ? hwSnap.data().value : null;
    const hw = Number.isInteger(hwRaw) && hwRaw > 0 ? hwRaw : null;

    // The stale-probe guard fires whenever the counter is absent, EVEN WITH a
    // high-water doc: an issuer that advanced the counter but not the high-water
    // (a client still on a pre-TASK-4832 bundle mid-rollout) is only visible
    // through a FRESH ticket probe. One extra read, on the rare escape-hatch
    // path only. Measured: skipping it here re-issued a number in the STALE
    // PROBE E2E.
    if (!snap.exists() && !probeFreshForThisAttempt) {
      const e = new Error('slip counter is absent — re-probing the live max before seeding it');
      e.code = 'failed-precondition';   // in COUNTER_RETRYABLE: the loop re-runs the thunk
      e.slipReprobe = true;
      throw e;
    }
    const raw = snap.exists() ? Number(snap.data().value) : null;
    // A ticket mark at/above the rules CREATE ceiling can never be a legitimately
    // issued number worth seeding from (no seed there is writable), and
    // `slipMaxNo` is client-writable. With a sane high-water doc to fall back on,
    // such a mark is IGNORED rather than allowed to hard-stop the escape hatch
    // (COD round 2, Independent). Without one, decideSlipCounterBase still
    // refuses by name.
    let ticketMax = Number.isFinite(observedMax) ? observedMax : null;
    let ticketMaxIgnored = null;
    if (ticketMax !== null && ticketMax >= SLIP_COUNTER_MAX_SEED && hw !== null && hw < SLIP_COUNTER_MAX_SEED) {
      ticketMaxIgnored = ticketMax;
      ticketMax = null;
    }
    const hwIsMax = hw !== null && (ticketMax === null || hw >= ticketMax);
    const liveMax = hwIsMax ? hw : ticketMax;
    const decision = decideSlipCounterBase({
      current: Number.isFinite(raw) ? raw : null,
      observedMax: liveMax,
      // A failed ticket probe is only fatal when there is no high-water doc to
      // stand in for it (it holds the max of everything this code ever issued).
      probeFailed: !!probeErr && hw === null,
    });
    if (hwIsMax) observedOnTicket = null;   // the refusal copy must not blame a ticket

    if (decision.action === 'refuse') {
      // Plain Error, no `.code` — allocateCounterWithRetry re-throws anything it
      // does not recognise on the FIRST attempt rather than burning 30s on a
      // decision that will not change.
      const err = new Error(decision.reason === 'seed-above-rules-ceiling'
        ? `Slip numbering looks wrong: ${observedOnTicket ? `ticket ${observedOnTicket}` : 'the slip high-water record'} says the highest slip is `
          + `SS-${decision.observedMax}, which is above the limit the database will accept for a new counter. `
          + `Check ${observedOnTicket ? 'that ticket' : 'it'} before recording anything else.`
        : 'Could not read or derive a slip number — ' + (probeErr?.message || probeErr));
      // These two messages were WRITTEN to be read by the biller: they name the
      // specific thing that is wrong. slipSaveFailedMessage surfaces them
      // verbatim instead of flattening them into the generic copy.
      err.slipNumberingRefused = true;
      throw err;
    }

    const value = decision.base + 1;
    txn.set(counterRef, {
      value,
      updatedAt: Timestamp.now(),
      // seededFrom is forensics: it answers "did this counter start from the live
      // max or from the empty-tenant seed" without re-deriving it later. Keyed on
      // snap.exists(), not on the decision, because that is what makes the write
      // a rules CREATE rather than a rules UPDATE.
      ...(snap.exists() ? {} : { createdAt: Timestamp.now(), seededFrom: decision.base })
    }, { merge: true });
    // The high-water mark, in the SAME transaction: it lands iff the counter
    // advance lands. No-op when it is already higher (an ignored implausible
    // value); clamped to the rules' per-write step.
    const hwNext = nextSlipHighWater({ issued: value, hw, hwExists: hwSnap.exists() });
    if (hwNext.write) {
      txn.set(hwRef, {
        value: hwNext.value,
        updatedAt: Timestamp.now(),
        ...(hwNext.create ? { createdAt: Timestamp.now() } : {}),
      }, { merge: true });
    }
    return { value, decision, hwNext, ticketMaxIgnored };
  });
}

// ============================================
// CREATE SLIP — writes to tickets/{ticketId}/slips
// Returns: { slipId, slipNumber, ...data }
// ============================================
/**
 * Recompute slip totals and persist them on the parent ticket so BI and
 * exports can read totals without joining the slips subcollection.
 * Called after every create / update / void / lock.
 */
async function recomputeAndPersistTicketSlipTotals(tenantRaw, ticketId) {
  const tenant = resolveTenant(tenantRaw);
  try {
    const slips = await getSlipsForTicket(tenantRaw, ticketId);
    const totals = getRunningTotals(slips);
    // TASK-4832 — `slipMaxNo` is deliberately NOT written here. It is stamped by
    // the allocation transaction, atomically with the counter, and this
    // best-effort, unordered write must never be able to LOWER it (two
    // recomputes on one ticket can commit in reverse order; a hard-deleted top
    // slip would drop the max below a printed number — both COD P1s). One
    // writer, monotonic, or the probe cannot be trusted. Void / lock / update
    // never change a slip's NUMBER, so nothing here needs to touch it.
    await updateDoc(doc(db, 'tenants', tenant, 'tickets', ticketId), {
      slipsAggregated: {
        bblsTotal: totals.bblsTotal,
        slipCount: totals.slipCount,
        voidCount: totals.voidCount,
        updatedAt: Timestamp.now(),
      },
    });
  } catch (e) {
    // Aggregation is best-effort — slips collection remains source of truth.
    console.warn('[slipHelpers] recomputeAndPersistTicketSlipTotals failed:', e?.message || e);
  }
}

export async function createSlip(tenantRaw, ticketId, slipData) {
  const tenant = resolveTenant(tenantRaw);
  // ticketId is passed so the issued number is stamped onto the ticket as
  // `slipMaxNo` BEFORE addDoc: a rejected addDoc leaves a gap (harmless) and
  // never a slip whose number the probe cannot see.
  const slipNumber = await getNextSlipNumber(tenantRaw, ticketId);

  const docToWrite = {
    slipNumber,
    ticketId,
    leaseName: slipData.leaseName || '',
    wellName: slipData.wellName || '',
    wellApiNumber: slipData.wellApiNumber || '',
    materialType: slipData.materialType || '',
    tankInValue: slipData.tankInValue ?? null,
    tankInUnit: slipData.tankInUnit || 'bbls',
    tankInFeet: slipData.tankInFeet ?? null,
    tankInInches: slipData.tankInInches ?? null,
    tankOutValue: slipData.tankOutValue ?? null,
    tankOutUnit: slipData.tankOutUnit || 'bbls',
    tankOutFeet: slipData.tankOutFeet ?? null,
    tankOutInches: slipData.tankOutInches ?? null,
    bblsTransferred: slipData.bblsTransferred ?? null,
    customerRepName: slipData.customerRepName || '',
    notes: slipData.notes || '',
    photoUrls: slipData.photoUrls || [],
    lineItemRefs: slipData.lineItemRefs || [],
    driverId: slipData.driverId || null,
    driverName: slipData.driverName || null,
    truckId: slipData.truckId || null,
    truckName: slipData.truckName || null,
    gpsCoords: slipData.gpsCoords || null,
    status: 'open', // open | locked | void
    createdAt: Timestamp.now(),
    createdBy: slipData.createdBy || (window.AppState?.user?.email || 'unknown'),
    lockedAt: null,
    lockedBy: null,
    voidedAt: null,
    voidedBy: null,
    replacementSlipId: null
  };

  const slipRef = await addDoc(
    collection(db, 'tenants', tenant, 'tickets', ticketId, 'slips'),
    docToWrite
  );

  await recomputeAndPersistTicketSlipTotals(tenantRaw, ticketId);
  return { slipId: slipRef.id, ...docToWrite };
}

// ============================================
// LOAD SLIPS for a ticket, ordered by createdAt
// ============================================
export async function getSlipsForTicket(tenantRaw, ticketId) {
  const tenant = resolveTenant(tenantRaw);
  const q = query(
    collection(db, 'tenants', tenant, 'tickets', ticketId, 'slips'),
    orderBy('createdAt', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ slipId: d.id, ...d.data() }));
}

// ============================================
// LOCK SLIP — admin-only via UI gate; rules allow tenant members to update,
// so the lock is enforced at the UI layer for v1.
// ============================================
export async function lockSlip(tenantRaw, ticketId, slipId) {
  const tenant = resolveTenant(tenantRaw);
  const slipRef = doc(db, 'tenants', tenant, 'tickets', ticketId, 'slips', slipId);
  await updateDoc(slipRef, {
    status: 'locked',
    lockedAt: Timestamp.now(),
    lockedBy: window.AppState?.user?.email || 'unknown'
  });
  await recomputeAndPersistTicketSlipTotals(tenantRaw, ticketId);
}

// ============================================
// VOID SLIP — keeps the doc, marks as void. Optionally references the
// slipId of the corrected replacement.
// ============================================
export async function voidSlip(tenantRaw, ticketId, slipId, replacementSlipId = null) {
  const tenant = resolveTenant(tenantRaw);
  const slipRef = doc(db, 'tenants', tenant, 'tickets', ticketId, 'slips', slipId);
  await updateDoc(slipRef, {
    status: 'void',
    voidedAt: Timestamp.now(),
    voidedBy: window.AppState?.user?.email || 'unknown',
    replacementSlipId: replacementSlipId
  });
  await recomputeAndPersistTicketSlipTotals(tenantRaw, ticketId);
}

// ============================================
// UPDATE SLIP (driver edit before lock; admin override after)
// ============================================
export async function updateSlip(tenantRaw, ticketId, slipId, patch) {
  const tenant = resolveTenant(tenantRaw);
  const slipRef = doc(db, 'tenants', tenant, 'tickets', ticketId, 'slips', slipId);
  await updateDoc(slipRef, {
    ...patch,
    updatedAt: Timestamp.now(),
    updatedBy: window.AppState?.user?.email || 'unknown'
  });
  // bblsTransferred can change on update — recompute aggregate
  await recomputeAndPersistTicketSlipTotals(tenantRaw, ticketId);
}

// ============================================
// AGGREGATE — running total of bbls across non-void slips on a ticket
// ============================================
export function getRunningTotals(slips) {
  const active = (slips || []).filter(s => s.status !== 'void');
  let bblsTotal = 0;
  for (const s of active) {
    const v = parseFloat(s.bblsTransferred);
    if (!isNaN(v)) bblsTotal += v;
  }
  return {
    slipCount: active.length,
    voidCount: (slips || []).length - active.length,
    bblsTotal: Math.round(bblsTotal * 100) / 100
  };
}

// ============================================
// FT+IN → DECIMAL FT helper (for tank measurements)
// ============================================
export function feetInchesToDecimalFt(feet, inches) {
  const f = parseFloat(feet) || 0;
  const i = parseFloat(inches) || 0;
  return f + (i / 12);
}

// ============================================
// PRINTABLE HTML — single ship slip
// Returns a complete HTML document ready for window.open() + window.print()
// Tries to print onto a 4x6 thermal label or US Letter; thermal printers
// usually default to label width via the OS print dialog.
// ============================================
export function buildSlipHTML(opts) {
  const { slip, ticket, customer, company, runningTotal, includeRunningTotal, template } = opts;
  // Template controls layout (shipped 2026-04-29 v1.15.214). Defaults preserve
  // pre-template behavior so existing call sites without `template` keep working.
  const tpl = {
    showRunningTotal: true,
    showGpsCoords: true,
    showPhotos: true,
    showLineItems: true,
    customFooterText: '',
    ...(template || {})
  };
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  const created = slip.createdAt?.toDate ? slip.createdAt.toDate() : (slip.createdAt instanceof Date ? slip.createdAt : new Date());
  const dateStr = created.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });

  const tankInDisplay = formatTankReading(slip.tankInUnit, slip.tankInValue, slip.tankInFeet, slip.tankInInches);
  const tankOutDisplay = formatTankReading(slip.tankOutUnit, slip.tankOutValue, slip.tankOutFeet, slip.tankOutInches);

  const lineItemsRows = tpl.showLineItems !== false
    ? (slip.lineItemRefs || []).map(ref => `
        <tr>
          <td>${esc(ref.description || ref.type || '')}</td>
          <td style="text-align:right;">${esc(ref.qty || ref.hours || '')}</td>
          <td style="text-align:right;">${esc(ref.unit || '')}</td>
        </tr>
      `).join('')
    : '';

  const photoBlock = (tpl.showPhotos !== false && (slip.photoUrls || []).length > 0)
    ? `<div class="slip-photos">${slip.photoUrls.map(u => `<img src="${esc(u)}" alt="">`).join('')}</div>`
    : '';

  const voidBanner = slip.status === 'void'
    ? `<div class="void-watermark">VOID</div>` : '';

  const runningTotalRow = (tpl.showRunningTotal !== false && includeRunningTotal && runningTotal != null)
    ? `<tr><td style="font-weight:bold;">Ticket Running Total:</td><td style="text-align:right;font-weight:bold;">${esc(runningTotal)} bbls</td></tr>`
    : '';

  const gpsRow = (tpl.showGpsCoords !== false && slip.gpsCoords?.lat)
    ? `<tr><td>GPS</td><td>${esc(slip.gpsCoords.lat.toFixed(5))}, ${esc(slip.gpsCoords.lng.toFixed(5))} (±${Math.round(slip.gpsCoords.accuracy || 0)}m)</td></tr>`
    : '';

  const customFooter = tpl.customFooterText && tpl.customFooterText.trim()
    ? `<div class="section" style="margin-top:18px;font-size:9pt;color:#444;border-top:1px dashed #bbb;padding-top:10px;">${esc(tpl.customFooterText.trim())}</div>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Ship Slip ${esc(slip.slipNumber)}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, system-ui, Helvetica, Arial, sans-serif; margin: 0; padding: 16px; color: #111; font-size: 12pt; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 12px; }
    .company { font-weight: bold; font-size: 14pt; }
    .company-meta { font-size: 9pt; color: #555; line-height: 1.3; }
    .slip-meta { text-align: right; }
    .slip-num { font-weight: bold; font-size: 16pt; }
    .slip-date { font-size: 9pt; color: #555; }
    .section { margin: 12px 0; }
    .section-label { font-size: 9pt; text-transform: uppercase; letter-spacing: 0.5px; color: #555; margin-bottom: 2px; }
    .section-value { font-size: 12pt; }
    table.kv { width: 100%; border-collapse: collapse; margin: 12px 0; }
    table.kv td { padding: 4px 6px; border-bottom: 1px solid #eee; font-size: 11pt; }
    table.kv td:first-child { color: #555; width: 45%; }
    table.lines { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 10pt; }
    table.lines th, table.lines td { padding: 4px 6px; border-bottom: 1px solid #ddd; text-align: left; }
    table.lines th { background: #f5f5f5; font-weight: 600; }
    .signature-block { margin-top: 24px; border-top: 1px solid #ddd; padding-top: 12px; }
    .signature-line { display: inline-block; width: 60%; border-bottom: 1px solid #111; margin-right: 12px; vertical-align: bottom; height: 24px; }
    .slip-photos { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
    .slip-photos img { max-width: 48%; max-height: 200px; border: 1px solid #ddd; }
    .void-watermark { position: fixed; top: 40%; left: 50%; transform: translate(-50%, -50%) rotate(-25deg); font-size: 96pt; color: rgba(220, 38, 38, 0.25); font-weight: bold; pointer-events: none; }
    .no-print { text-align: center; margin-bottom: 16px; }
    .no-print button { padding: 10px 24px; background: #3B82F6; color: #fff; border: 0; border-radius: 6px; font-size: 14pt; cursor: pointer; }
    @media print { .no-print { display: none; } }
  </style>
</head>
<body>
  ${voidBanner}
  <div class="no-print">
    <button onclick="window.print()">Print Ship Slip</button>
  </div>
  <div class="header">
    <div>
      <div class="company">${esc(company?.name || '')}</div>
      <div class="company-meta">
        ${esc(company?.address || '')}<br>
        ${esc(company?.phone || '')}${company?.phone && company?.email ? ' · ' : ''}${esc(company?.email || '')}
      </div>
    </div>
    <div class="slip-meta">
      <div class="slip-num">${esc(slip.slipNumber)}</div>
      <div class="slip-date">${esc(dateStr)}</div>
      ${ticket?.invoiceNumber ? `<div class="slip-date">Ticket: ${esc(ticket.invoiceNumber)}</div>` : ''}
    </div>
  </div>

  <table class="kv">
    <tr><td>Customer</td><td>${esc(customer?.name || ticket?.customerName || '')}</td></tr>
    <tr><td>Lease / Well</td><td>${esc(slip.leaseName || '')}${slip.wellName ? ' — ' + esc(slip.wellName) : ''}</td></tr>
    ${slip.wellApiNumber ? `<tr><td>API #</td><td>${esc(slip.wellApiNumber)}</td></tr>` : ''}
    ${slip.materialType ? `<tr><td>Material</td><td>${esc(slip.materialType)}</td></tr>` : ''}
    ${slip.driverName ? `<tr><td>Driver</td><td>${esc(slip.driverName)}</td></tr>` : ''}
    ${slip.truckName ? `<tr><td>Vehicle</td><td>${esc(slip.truckName)}</td></tr>` : ''}
    <tr><td>Tank In</td><td>${esc(tankInDisplay || '—')}</td></tr>
    <tr><td>Tank Out</td><td>${esc(tankOutDisplay || '—')}</td></tr>
    <tr><td style="font-weight:bold;">Bbls Transferred (this slip)</td><td style="text-align:right;font-weight:bold;">${esc(slip.bblsTransferred ?? '—')}</td></tr>
    ${runningTotalRow}
    ${gpsRow}
  </table>

  ${lineItemsRows ? `
  <div class="section">
    <div class="section-label">Line Items on This Slip</div>
    <table class="lines">
      <thead><tr><th>Description</th><th style="text-align:right;">Qty</th><th style="text-align:right;">Unit</th></tr></thead>
      <tbody>${lineItemsRows}</tbody>
    </table>
  </div>` : ''}

  ${slip.notes ? `<div class="section"><div class="section-label">Notes</div><div class="section-value">${esc(slip.notes)}</div></div>` : ''}

  ${photoBlock}

  <div class="signature-block">
    <div class="section-label">Customer Representative</div>
    <div style="margin-top:8px;">
      <span class="signature-line"></span>
      <span style="font-size:10pt;color:#555;">${esc(slip.customerRepName || '')}</span>
    </div>
  </div>

  ${customFooter}
</body>
</html>`;
}

function formatTankReading(unit, value, feet, inches) {
  if (unit === 'ft_in') {
    if (feet == null && inches == null) return '';
    return `${feet || 0}' ${inches || 0}"`;
  }
  if (value == null || value === '') return '';
  return `${value} bbls`;
}

// ============================================
// PRINT — opens a new window with the slip HTML and triggers print dialog.
// ============================================
export function printSlip(opts) {
  const html = buildSlipHTML(opts);
  const win = window.open('', '_blank');
  if (!win) {
    console.warn('[slipHelpers] window.open blocked by browser');
    return false;
  }
  win.document.write(html);
  win.document.close();
  return true;
}

// Belt-and-suspenders: also expose on window so callers can use either pattern.
if (typeof window !== 'undefined') {
  window.SlipHelpers = {
    getNextSlipNumber,
    // TASK-4832 — diagnostics + the handle tests/slip-counter-self-heal.spec.js
    // asserts on. NOT the transport: billing.js `import`s these.
    decideSlipCounterBase,
    slipSaveFailedMessage,
    parseSlipNo,
    // Exposed so the suite can TIME the probe itself rather than inferring its
    // cost from an allocation latency the transaction round-trip dominates.
    probeMaxSlipNo: _probeMaxSlipNo,
    SLIP_NO_SEED,
    SLIP_SELF_HEAL_MAX_JUMP,
    SLIP_COUNTER_MAX_SEED,
    SLIP_HIGH_WATER_ID,
    nextSlipHighWater,
    slipNumberWitnessed,
    createSlip,
    getSlipsForTicket,
    lockSlip,
    voidSlip,
    updateSlip,
    getRunningTotals,
    feetInchesToDecimalFt,
    buildSlipHTML,
    printSlip
  };
}
